import React, {useEffect} from 'react';
import {Typography} from '@mui/material';
import ProjectHeader from '../components/project/ProjectHeader';
import ProjectTextBox from '../components/project/ProjectTextBox';
import ProjectImage from '../components/project/ProjectImage';
import ProjectTwoColumns from '../components/project/ProjectTwoColumns';
import ProjectPersona from '../components/project/ProjectPersona';
import ProjectQuestions from '../components/project/ProjectQuestions';
import Footer from '../components/Footer';

function AdoptMe() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            <ProjectHeader
                backgroundImage="/assets/images/cover-am.png"
                projectName="Adopt Me"
                role="UX/UI Designer"
                duration="Aug – Sep 2021"
            />

            <ProjectTwoColumns>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Project Vision</Typography>
                    <Typography variant="body1">
                        <strong>Adopt Me</strong> is a platform for the adoption of dogs. The typical user is between 18–55 years old and most users are people that need some company or that choose to adopt dogs offering them a new life.
                    </Typography>
                </div>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Challenges</Typography>
                    <Typography variant="body1">
                        Create a website that is user-friendly by providing clear navigation and offering a fast process for dog adoption.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectImage url="/assets/images/desktop-mobile-am.png" alt="Adopt Me desktop and mobile views"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Kickoff</Typography>
                <Typography variant="body1">
                    In this project, I conducted <strong>user interviews</strong>, which I then turned into empathy maps to better understand the target user and their needs. I discovered that some users need a dog to improve their state of stress or depression. However, many adoption websites are confusing to navigate, creating inconvenience to users. I then have tried to improve their user experience.
                </Typography>
                <ProjectQuestions questions={[
                    'What is the product and who is it for?',
                    'What do our users need most?',
                    'What challenges could we face moving forward?',
                    'Who do we see as our biggest competitors?',
                ]}/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Affinity Diagram</Typography>
                <Typography variant="body1">
                    I used an <strong>affinity diagram</strong> to separate the data into groups of tasks which were further categorized by high-level goals for improvement in efficiency, process, depth, and familiarity. Recognizing the conflicts of interest from each audience allowed me to focus on shaping user goals.
                </Typography>
                <ProjectImage url="/assets/images/affinity diagram.am(2).png" alt="Affinity diagram"/>
            </ProjectTextBox>

            <ProjectTwoColumns withBackground title="Users">
                <div>
                    <ProjectPersona url="/assets/images/david.png" name="Primary">
                        <li><strong>Name:</strong> David</li>
                        <li><strong>Age:</strong> 26</li>
                        <li><strong>Occupation:</strong> Production worker</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        David is a busy professional and animal lover. He will soon go to live alone in a house in the mountains and would like to adopt a puppy to share this new experience and to have great company.
                    </Typography>
                </div>
                <div>
                    <ProjectPersona url="/assets/images/agata.png" name="Secondary">
                        <li><strong>Name:</strong> Agata</li>
                        <li><strong>Age:</strong> 34</li>
                        <li><strong>Occupation:</strong> Pastry Chef</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        She lives with her daughter in California and they have a great relationship, but her daughter is very sad since her father died. For this Agata wants to give her a puppy, in particular, an unloved puppy ready to be loved.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Competitive Analysis</Typography>
                <Typography variant="body1">
                    I have created a <strong>competitive audit</strong> by examining several potential competitors and I have found that no one allows making an appointment online. Most of the features between competitors were very similar, the main differences I noticed were:
                </Typography>
                <Typography variant="body1">~ Easily Accessible vs Hardly Accessible</Typography>
                <Typography variant="body1">~ Too Many Steps vs Simplified Interaction</Typography>
                <Typography variant="body1">~ Distracting Interface vs Minimalistic Interface</Typography>
                <ProjectImage url="/assets/images/audit.am.png" alt="Competitive audit"/>
            </ProjectTextBox>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Sitemap</Typography>
                <Typography variant="body1">
                    I created a user journey map to help identify possible pain points and improvement opportunities. Difficulty with website navigation was a primary pain point for users, so I used that knowledge to create a <strong>sitemap</strong>.
                </Typography>
                <Typography variant="body1">
                    My goal here was to make strategic information architecture decisions that would improve overall website navigation.
                </Typography>
                <ProjectImage url="/assets/images/journeymap.am(2).png" alt="Journey map"/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Wireframe</Typography>
                <Typography variant="body1">
                    Moving from paper to digital wireframes made it easy to understand how the redesign could help address users pain points and improve user experience. Prioritizing useful button locations and visual element placement on the home page was a key part of my strategy.
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/wireframe-am.png" alt="Wireframes"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Interaction</Typography>
                <Typography variant="body1">
                    After creating my prototype from low fidelity wireframes, I asked 5 participants to run through different scenarios in my prototype in hopes of garnering enough <strong>feedback</strong> to use for my next set of design interaction.
                </Typography>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Mockups</Typography>
                <Typography variant="body1">
                    Based on the insights from the usability study, I made some changes to improve the adoption flow. One change I made was the addition of the puppy location. This allowed users to choose the puppy based on their distance.
                </Typography>
                <Typography variant="body1">
                    Another change I made was the addition of the "filter" menu. This gave more freedom to users filtering adoptions based on their preferences.
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/mockup(2).png" alt="Mockups"/>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Design System</Typography>
                <ProjectImage url="/assets/images/Designsystem.am.png" alt="Design system"/>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/secondmockups.am.png" alt="Additional mockups"/>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Takeaways</Typography>
                <Typography variant="body1">
                    I created this app during my Google UX Designer course on Coursera. Adopt Me is very close and dear to my heart because I love animals and I personally had the opportunity to give a better life to a puppy. I learned to understand what the problem was and this helped me find workable and useful solutions for a great user experience.
                </Typography>
            </ProjectTextBox>

            <Footer/>
        </div>
    );
}

export default AdoptMe;
